import {
    candlesCsv,
    candlesJson,
    eventFolder,
    fieldsFor,
    fillCandles,
    infoJson,
    marketFileName,
    mergeCandles,
    parseRows,
    pricesCsv,
    pricesFileName,
    pricesJson,
    selectionInfoJson,
    selectionZipFileName,
    slugify,
    zipFileName,
    type MarketCloses,
    type SelectionFile,
    type SelectionGroup,
} from './buildFile';
import { marketWindow, pagesOf, type TimeWindow } from './exportPlan';
import type {
    Candle,
    ExportEventRef,
    ExportMarket,
    ExportSettings,
    PageRequest,
    RawRow,
} from './exportTypes';
import { ZipWriter } from './zip';

export const PAGE_WORKERS = 3;
export const PAGE_RETRIES = 2;

export interface ExportProgress {
    done: number;
    total: number;
}

export interface ExportDeps {
    loadPage: (request: PageRequest, signal: AbortSignal) => Promise<RawRow[]>;
    onProgress: (progress: ExportProgress) => void;
    signal: AbortSignal;
    retryDelayMs?: number;
    isFatal?: (error: unknown) => boolean;
}

export interface ExportResult {
    filename: string;
    blob: Blob;
    rows: number;
}

export const MIME = {
    csv: 'text/csv;charset=utf-8;',
    json: 'application/json;charset=utf-8;',
};

export function aborted(): DOMException {
    return new DOMException('Aborted', 'AbortError');
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal.aborted) {
            reject(aborted());
            return;
        }
        const timer = setTimeout(resolve, ms);
        signal.addEventListener(
            'abort',
            () => {
                clearTimeout(timer);
                reject(aborted());
            },
            { once: true }
        );
    });
}

export interface RetryDeps {
    signal: AbortSignal;
    retryDelayMs?: number;
    isFatal?: (error: unknown) => boolean;
}

// One page request with PAGE_RETRIES retries, a growing pause between them,
// and no retry after an abort or a fatal error. Shared with the trader download.
export async function withRetry<T>(
    load: (signal: AbortSignal) => Promise<T>,
    deps: RetryDeps
): Promise<T> {
    for (let attempt = 0; ; attempt += 1) {
        if (deps.signal.aborted) throw aborted();
        try {
            return await load(deps.signal);
        } catch (error) {
            const stop =
                (error as { name?: string })?.name === 'AbortError' ||
                deps.isFatal?.(error) === true ||
                attempt >= PAGE_RETRIES;
            if (stop) throw error;
            await wait((deps.retryDelayMs ?? 700) * (attempt + 1), deps.signal);
        }
    }
}

function loadWithRetry(
    request: PageRequest,
    deps: ExportDeps
): Promise<RawRow[]> {
    return withRetry(signal => deps.loadPage(request, signal), deps);
}

async function loadMarket(
    settings: ExportSettings,
    window: TimeWindow,
    market: ExportMarket,
    fields: readonly string[],
    deps: ExportDeps,
    tick: () => void
): Promise<Candle[]> {
    const pages = pagesOf(window, settings.intervalMinutes);
    const results: Candle[][] = new Array(pages.length);
    let next = 0;
    const worker = async () => {
        while (next < pages.length) {
            const index = next;
            next += 1;
            const rows = await loadWithRetry(
                {
                    conditionId: market.conditionId,
                    side: settings.side,
                    startMs: pages[index].startMs,
                    endMs: pages[index].endMs,
                    intervalMinutes: settings.intervalMinutes,
                },
                deps
            );
            results[index] = parseRows(rows, fields);
            tick();
        }
    };
    await Promise.all(
        Array.from({ length: Math.min(PAGE_WORKERS, pages.length) }, worker)
    );
    const merged = mergeCandles(results);
    return settings.fill
        ? fillCandles(merged, window, settings.intervalMinutes)
        : merged;
}

interface ExportJob {
    market: ExportMarket;
    window: TimeWindow;
}

interface JobGroup {
    event: ExportEventRef | null;
    jobs: ExportJob[];
}

function groupJobs(jobs: readonly ExportJob[]): JobGroup[] {
    const byEvent = new Map<string, JobGroup>();
    for (const job of jobs) {
        const key = job.market.event?.id ?? '';
        let group = byEvent.get(key);
        if (!group) {
            group = { event: job.market.event ?? null, jobs: [] };
            byEvent.set(key, group);
        }
        group.jobs.push(job);
    }
    return [...byEvent.values()];
}

function eventOf(group: JobGroup, settings: ExportSettings): ExportEventRef {
    return (
        group.event ?? {
            id: settings.eventId,
            slug: settings.eventSlug,
            title: settings.eventTitle,
        }
    );
}

function settingsFor(
    settings: ExportSettings,
    event: ExportEventRef | null
): ExportSettings {
    return event
        ? {
              ...settings,
              eventId: event.id,
              eventSlug: event.slug,
              eventTitle: event.title,
          }
        : settings;
}

function spanOf(jobs: readonly ExportJob[]): TimeWindow {
    let startMs = jobs[0].window.startMs;
    let endMs = jobs[0].window.endMs;
    for (const job of jobs) {
        startMs = Math.min(startMs, job.window.startMs);
        endMs = Math.max(endMs, job.window.endMs);
    }
    return { startMs, endMs };
}

function folderNames(
    groups: readonly JobGroup[],
    settings: ExportSettings
): string[] {
    const used = new Set<string>();
    return groups.map(group => {
        const event = eventOf(group, settings);
        const base = eventFolder(event);
        let name = base;
        if (used.has(name)) name = `${base}_${slugify(event.id)}`;
        for (let n = 2; used.has(name); n += 1) name = `${base}_${n}`;
        used.add(name);
        return name;
    });
}

function priceRows(series: readonly MarketCloses[]): number {
    const times = new Set<number>();
    for (const s of series) for (const t of s.closes.keys()) times.add(t);
    return times.size;
}

export async function runExport(
    settings: ExportSettings,
    window: TimeWindow,
    deps: ExportDeps
): Promise<ExportResult> {
    const fields = fieldsFor(settings.side);
    const jobs: ExportJob[] = settings.markets.flatMap(market => {
        const own = marketWindow(window, market, settings.intervalMinutes);
        return own ? [{ market, window: own }] : [];
    });
    if (jobs.length === 0) {
        throw new Error('No market has data in this time range.');
    }
    const total = jobs.reduce(
        (sum, job) =>
            sum + pagesOf(job.window, settings.intervalMinutes).length,
        0
    );
    let done = 0;
    const tick = () => {
        done += 1;
        deps.onProgress({ done, total });
    };
    deps.onProgress({ done, total });

    const groups = groupJobs(jobs);
    const multi = groups.length > 1;

    const loadJob = (job: ExportJob) =>
        loadMarket(settings, job.window, job.market, fields, deps, tick);

    const loadCloses = async (group: JobGroup): Promise<MarketCloses[]> => {
        const series: MarketCloses[] = [];
        for (const job of group.jobs) {
            const candles = await loadJob(job);
            series.push({
                market: job.market,
                closes: new Map(candles.map(c => [c.t, c.close])),
            });
        }
        return series;
    };

    const pricesText = (
        own: ExportSettings,
        span: TimeWindow,
        series: readonly MarketCloses[]
    ) =>
        settings.file === 'csv'
            ? pricesCsv(series)
            : pricesJson(own, span, series);

    const render = (
        own: ExportSettings,
        job: ExportJob,
        candles: readonly Candle[]
    ) =>
        settings.file === 'csv'
            ? candlesCsv(candles, fields)
            : candlesJson(own, job.window, job.market, candles, fields);

    if (!multi && settings.data === 'prices') {
        const own = settingsFor(settings, groups[0].event);
        const series = await loadCloses(groups[0]);
        return {
            filename: pricesFileName(own, window),
            blob: new Blob([pricesText(own, window, series)], {
                type: MIME[settings.file],
            }),
            rows: priceRows(series),
        };
    }

    if (jobs.length === 1) {
        const job = jobs[0];
        const own = settingsFor(settings, groups[0].event);
        const candles = await loadJob(job);
        return {
            filename: marketFileName(own, job.window, job.market),
            blob: new Blob([render(own, job, candles)], {
                type: MIME[settings.file],
            }),
            rows: candles.length,
        };
    }

    const zip = new ZipWriter();
    const folders = folderNames(groups, settings);
    const listed: { name: string; market: ExportMarket; rows: number }[] = [];
    const selection: SelectionGroup[] = [];
    let rows = 0;
    for (let index = 0; index < groups.length; index += 1) {
        const group = groups[index];
        const own = settingsFor(settings, group.event);
        const prefix = multi ? `${folders[index]}/` : '';
        const files: SelectionFile[] = [];
        if (settings.data === 'prices') {
            const span = spanOf(group.jobs);
            const series = await loadCloses(group);
            const name = `${prefix}${pricesFileName(own, span)}`;
            const count = priceRows(series);
            await zip.add(name, pricesText(own, span, series));
            files.push({
                name,
                rows: count,
                window: span,
                markets: group.jobs.map(job => job.market),
            });
            rows += count;
        } else {
            for (const job of group.jobs) {
                const candles = await loadJob(job);
                const name = `${prefix}${marketFileName(own, job.window, job.market)}`;
                await zip.add(name, render(own, job, candles));
                files.push({
                    name,
                    rows: candles.length,
                    window: job.window,
                    markets: [job.market],
                });
                listed.push({ name, market: job.market, rows: candles.length });
                rows += candles.length;
            }
        }
        selection.push({
            event: eventOf(group, settings),
            folder: folders[index],
            files,
        });
    }

    if (multi) {
        await zip.add(
            'info.json',
            selectionInfoJson(settings, window, selection)
        );
        return {
            filename: selectionZipFileName(settings, window, groups.length),
            blob: zip.finish(),
            rows,
        };
    }

    const own = settingsFor(settings, groups[0].event);
    await zip.add('info.json', infoJson(own, window, listed));
    return {
        filename: zipFileName(own, window),
        blob: zip.finish(),
        rows,
    };
}

export function saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
